/**
 * @file BannerFormDialog.tsx
 * @brief Create / edit sheet for home-screen promotional banners.
 *
 * @details
 * Uploads images via `POST /api/banners/upload`, then persists the
 * banner row through the existing admin CRUD endpoints. Field limits
 * mirror the mobile Super Admin modal. Chrome matches other FormSheets.
 *
 * @dependencies
 * - react-hook-form, @hookform/resolvers/zod
 *
 * @references
 * - FRONTEND/src/screens/SuperAdmin/components/BannerManagementModal.tsx
 * - BACKEND/src/features/banners/routes.js
 *
 * @author DES
 * @date 07-06-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 * @updated 01-10-2026 — form standard: palette frame, Image / Content tabs
 *   (the full-width preview made it taller than a screen), Enabled as the
 *   header pill
 */

import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ImageIcon, ImagePlus, LoaderCircle, Trash2, Type } from "lucide-react";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  useCreateBanner,
  useDeleteBanner,
  useUpdateBanner,
  useUploadBannerImage,
} from "../api";
import type { Banner } from "../schemas";

const FormSchema = z.object({
  title: z.string().max(60).optional(),
  subtitle: z.string().max(100).optional(),
  cta_text: z.string().max(20).optional(),
  target_route: z.string().optional(),
  is_enabled: z.boolean(),
  display_order: z.coerce.number().int().min(0),
});

type FormValues = z.infer<typeof FormSchema>;

interface BannerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  banner?: Banner | null;
}

export function BannerFormDialog({
  open,
  onOpenChange,
  banner,
}: BannerFormDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Tabs (DES 01-10-2026: form standard).
  const [tab, setTab] = useState<BannerFormTab>("image");

  const uploadImage = useUploadBannerImage();
  const createBanner = useCreateBanner();
  const updateBanner = useUpdateBanner();
  const deleteBanner = useDeleteBanner();

  const isSaving =
    uploadImage.isPending || createBanner.isPending || updateBanner.isPending;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      title: "",
      subtitle: "",
      cta_text: "",
      target_route: "",
      is_enabled: true,
      display_order: 0,
    },
  });

  useEffect(() => {
    if (!open) return;

    setServerError(null);
    setPendingFile(null);
    setTab("image");

    if (banner) {
      setImageUrl(banner.image_url);
      setLocalPreview(null);
      form.reset({
        title: banner.title ?? "",
        subtitle: banner.subtitle ?? "",
        cta_text: banner.cta_text ?? "",
        target_route: banner.target_route ?? "",
        is_enabled: banner.is_enabled,
        display_order: banner.display_order,
      });
      return;
    }

    setImageUrl("");
    setLocalPreview(null);
    form.reset({
      title: "",
      subtitle: "",
      cta_text: "",
      target_route: "",
      is_enabled: true,
      display_order: 0,
    });
  }, [open, banner, form]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const handlePickFile = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: "Invalid file",
        description: "Please choose an image file.",
      });
      return;
    }

    if (localPreview) URL.revokeObjectURL(localPreview);
    setPendingFile(file);
    setLocalPreview(URL.createObjectURL(file));
  };

  const onSubmit = async (values: FormValues) => {
    setServerError(null);

    try {
      let finalImageUrl = imageUrl;

      if (pendingFile) {
        const uploaded = await uploadImage.mutateAsync(pendingFile);
        finalImageUrl = uploaded.imageUrl;
      }

      if (!finalImageUrl) {
        setServerError("Please upload a banner image.");
        // Show the tab the missing image belongs to.
        setTab("image");
        return;
      }

      const payload = {
        image_url: finalImageUrl,
        title: values.title?.trim() || undefined,
        subtitle: values.subtitle?.trim() || undefined,
        cta_text: values.cta_text?.trim() || undefined,
        target_route: values.target_route?.trim() || undefined,
        is_enabled: values.is_enabled,
        display_order: values.display_order,
      };

      if (banner) {
        await updateBanner.mutateAsync({ id: banner.id, input: payload });
        toast({ variant: "success", title: "Banner updated" });
      } else {
        await createBanner.mutateAsync(payload);
        toast({ variant: "success", title: "Banner created" });
      }

      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : "Please try again.";
      setServerError(message);
      toast({
        variant: "destructive",
        title: "Save failed",
        description: message,
      });
    }
  };

  const handleDelete = async () => {
    if (!banner) return;
    try {
      await deleteBanner.mutateAsync(banner.id);
      toast({ variant: "success", title: "Banner deleted" });
      setConfirmDelete(false);
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const previewSrc = localPreview || imageUrl || null;

  return (
    <>
      <FormSheet open={open} onOpenChange={onOpenChange}>
        {/* Opens like Cmd+K, as Edit user (DES 01-10-2026: form standard). */}
        <FormSheetContent side="palette">
          <form
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
            // Every validated field is on Content: show it if one failed.
            onSubmit={form.handleSubmit(onSubmit, () => setTab("content"))}
            noValidate
          >
            <FormSheetFrame>
              <FormSheetHeader>
                {/* Enabled sits beside the title as a pill, as Edit company
                    (DES 01-10-2026: form standard). It was a switch in its
                    own Visibility section. */}
                <div className="flex items-start justify-between gap-6">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <FormSheetTitle>
                      {banner ? "Edit banner" : "Create banner"}
                    </FormSheetTitle>
                    <FormSheetDescription>
                      Manage the promotional slide shown on the mobile home
                      screen. Disabled banners stay in the list but do not
                      appear in the app.
                    </FormSheetDescription>
                  </div>
                  {/* mr-10 clears the sheet's close ✕ in the corner. */}
                  <div className="mr-10 flex shrink-0 items-center">
                    <Select
                      value={form.watch("is_enabled") ? "enabled" : "disabled"}
                      onValueChange={(v) =>
                        form.setValue("is_enabled", v === "enabled", {
                          shouldDirty: true,
                        })
                      }
                    >
                      <SelectTrigger
                        aria-label="Banner visibility"
                        className="!h-9 !w-auto gap-2 !rounded-full !px-4 text-sm font-semibold focus:!ring-0 focus:!ring-offset-0 focus-visible:bg-foreground/5 focus-visible:outline-none"
                      >
                        {/* A div: the trigger line-clamps its direct spans. */}
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className={cn(
                              "block h-2 w-2 shrink-0 rounded-full",
                              form.watch("is_enabled")
                                ? "bg-success"
                                : "bg-muted-foreground",
                            )}
                          />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enabled">Enabled</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </FormSheetHeader>

              <FormSheetTabs
                tabs={BANNER_FORM_TABS}
                value={tab}
                onChange={setTab}
                label={banner ? "Edit banner" : "Create banner"}
              />

              <FormSheetBody>
                {/* Both tabs stay mounted, so values and errors survive
                    switching tabs; hidden panels are display:none. */}
                <div {...formSheetTabPanel(tab, "image")}>
                  <FormSheetSection
                    hideHeading
                    title="Image"
                    subtitle="Shown on the mobile home carousel."
                  >
                    <FormSheetFields className="grid-cols-1">
                      <FormSheetField span={2}>
                        {/* Capped width: at the palette's full width the
                            1.1 preview was taller than the screen. */}
                        <div className="mx-auto w-full max-w-sm">
                          {previewSrc ? (
                            <img
                              src={previewSrc}
                              alt="Banner preview"
                              className="aspect-[1.1] w-full rounded-lg object-cover"
                            />
                          ) : (
                            <div className="flex aspect-[1.1] items-center justify-center rounded-lg bg-muted/40 text-sm text-muted-foreground">
                              No image selected
                            </div>
                          )}
                        </div>
                      </FormSheetField>
                      {/* A compact pill under the preview, not a full-width bar
                          (DES 01-10-2026: form standard). */}
                      <FormSheetField span={2} className="flex justify-center">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) =>
                            handlePickFile(e.target.files?.[0] ?? null)
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isSaving}
                          className={cn(formSheetActionClass, "!h-11 !w-auto !px-6 !text-sm")}
                        >
                          {uploadImage.isPending ? (
                            <LoaderCircle className="h-4 w-4 animate-spin" />
                          ) : (
                            <ImagePlus className="h-4 w-4" />
                          )}
                          {previewSrc ? "Change image" : "Upload image"}
                        </Button>
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>
                </div>

                <div {...formSheetTabPanel(tab, "content")}>
                  <FormSheetSection
                    hideHeading
                    title="Content"
                    subtitle="Copy and deep-link for the slide."
                  >
                    {/* Long text two columns wide, short fields beside it
                        (DES 01-10-2026: form standard). */}
                    <FormSheetFields className="sm:grid-cols-3">
                      <FormSheetField span={2}>
                        <FormFieldGroup
                          label="Title (max 60)"
                          htmlFor="banner-title"
                          error={form.formState.errors.title?.message}
                        >
                          <Input
                            capitalizeFirst
                            id="banner-title"
                            maxLength={60}
                            {...form.register("title")}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup
                          label="CTA text (max 20)"
                          htmlFor="banner-cta"
                          error={form.formState.errors.cta_text?.message}
                        >
                          <Input
                            id="banner-cta"
                            maxLength={20}
                            {...form.register("cta_text")}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField span={2}>
                        <FormFieldGroup
                          label="Subtitle (max 100)"
                          htmlFor="banner-subtitle"
                          error={form.formState.errors.subtitle?.message}
                        >
                          <Input
                            id="banner-subtitle"
                            maxLength={100}
                            {...form.register("subtitle")}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField>
                        <FormFieldGroup
                          label="Display order"
                          htmlFor="banner-order"
                          error={form.formState.errors.display_order?.message}
                        >
                          <Input
                            id="banner-order"
                            type="number"
                            min={0}
                            {...form.register("display_order")}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                      <FormSheetField span={2}>
                        <FormFieldGroup
                          label="Target route (optional)"
                          htmlFor="banner-route"
                          hint="Internal: screen name such as Billing or Sessions. External: full URL starting with https://."
                        >
                          <Input
                            id="banner-route"
                            placeholder="Billing, UsageOverview, or https://example.com"
                            {...form.register("target_route")}
                          />
                        </FormFieldGroup>
                      </FormSheetField>
                    </FormSheetFields>
                  </FormSheetSection>

                  {banner ? (
                    <FormSheetSection
                      title="Remove"
                      subtitle="Permanently drops this slide from the mobile home carousel."
                    >
                      <FormSheetFields className="grid-cols-1">
                        <FormSheetField span={2} padded={false}>
                          <Button
                            type="button"
                            variant="ghost"
                            className={cn(
                              formSheetActionClass,
                              "text-destructive hover:bg-destructive/5 hover:text-destructive",
                            )}
                            onClick={() => setConfirmDelete(true)}
                            disabled={isSaving || deleteBanner.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete banner
                          </Button>
                        </FormSheetField>
                      </FormSheetFields>
                    </FormSheetSection>
                  ) : null}
                </div>

                {serverError ? (
                  <FormSheetFields className="grid-cols-1">
                    <FormSheetAlert>{serverError}</FormSheetAlert>
                  </FormSheetFields>
                ) : null}
              </FormSheetBody>

              <FormSheetFooter>
                <FormSheetFooterCell>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    disabled={isSaving}
                    className={formSheetActionClass}
                  >
                    Cancel
                  </Button>
                </FormSheetFooterCell>
                <FormSheetFooterCell>
                  <Button
                    type="submit"
                    disabled={isSaving}
                    className={cn(
                      formSheetActionClass,
                      "bg-primary text-primary-foreground hover:bg-primary/90",
                    )}
                  >
                    {isSaving ? (
                      <>
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : banner ? (
                      "Save changes"
                    ) : (
                      "Create banner"
                    )}
                  </Button>
                </FormSheetFooterCell>
              </FormSheetFooter>
            </FormSheetFrame>
          </form>
        </FormSheetContent>
      </FormSheet>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this banner?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the slide from the mobile home screen. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete banner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

type BannerFormTab = "image" | "content";

// Icons per tab, as Edit user (DES 01-10-2026: form standard).
const BANNER_FORM_TABS: FormSheetTab<BannerFormTab>[] = [
  { id: "image", label: "Image", icon: ImageIcon },
  { id: "content", label: "Content", icon: Type },
];
